/**
 * Prysm Registration Database Utility
 * 
 * This module provides a promise-based interface to localStorage using a simple wrapper.
 * It handles all data storage operations for the registration application with
 * comprehensive error handling and logging.
 * 
 * @author OurPrysm Development Team
 * @version 2.0.0
 */

import clientLogger from './clientLogger';

/**
 * Database configuration constants
 */
const DB_NAME = 'prysm-registration-db';
const DB_VERSION = 2;

/**
 * Object store names (same as current localStorage keys)
 */
const STORE_NAMES = [
    'registration_data',
    'selfie_data',
    'license_data',
    'payment_result',
    'square_nonce_info',
    'selected_plan',
    'promo_data',
    'sms_send_result',
    'visitorUUID',
    'selfie_pose',
    'phoneNumber'
];

/**
 * PrysmDatabase class for managing localStorage operations
 * Provides a clean API for all data storage needs with comprehensive error handling
 */
class PrysmDatabase {
    constructor() {
        this.isInitialized = true; // localStorage is always available
        this.useFallback = false; // Not needed for localStorage
        console.log('[PrysmDatabase] Initialized with localStorage');
    }

    /**
     * Initialize database (no-op for localStorage)
     * @returns {Promise<boolean>} Always resolves to true
     */
    async initDB() {
        console.log('[PrysmDatabase] localStorage is always available');
        return Promise.resolve(true);
    }

    /**
     * Get data from localStorage
     * @param {string} storeName - Name of the "store" (used for key prefix)
     * @param {string} key - Key to retrieve
     * @returns {Promise<any>} Retrieved data
     */
    async get(storeName, key) {
        try {
            const localStorageKey = `${DB_NAME}_${storeName}_${key}`;
            const data = localStorage.getItem(localStorageKey);

            if (data === null) {
                console.log(`[PrysmDatabase] No data found for ${key} in ${storeName}`);
                return undefined;
            }

            const result = JSON.parse(data);
            console.log(`[PrysmDatabase] Retrieved ${key} from ${storeName}`);
            return result;
        } catch (error) {
            console.error(`[PrysmDatabase] Error retrieving ${key} from ${storeName}:`, error);
            clientLogger.logError('database_get_error', {
                storeName,
                key,
                error: error.message,
                stack: error.stack
            });
            throw new Error(`Storage operation failed: ${error.message}`);
        }
    }

    /**
     * Store data in localStorage
     * @param {string} storeName - Name of the "store" (used for key prefix)
     * @param {string} key - Key to store under
     * @param {any} value - Data to store
     * @returns {Promise<void>}
     */
    async set(storeName, key, value) {
        try {
            const localStorageKey = `${DB_NAME}_${storeName}_${key}`;
            localStorage.setItem(localStorageKey, JSON.stringify(value));
            console.log(`[PrysmDatabase] Stored ${key} in ${storeName}`);
        } catch (error) {
            console.error(`[PrysmDatabase] Error storing ${key} in ${storeName}:`, error);
            clientLogger.logError('database_set_error', {
                storeName,
                key,
                error: error.message,
                stack: error.stack
            });
            throw new Error(`Storage operation failed: ${error.message}`);
        }
    }

    /**
     * Delete data from localStorage
     * @param {string} storeName - Name of the "store" (used for key prefix)
     * @param {string} key - Key to delete
     * @returns {Promise<void>}
     */
    async delete(storeName, key) {
        try {
            const localStorageKey = `${DB_NAME}_${storeName}_${key}`;
            localStorage.removeItem(localStorageKey);
            console.log(`[PrysmDatabase] Deleted ${key} from ${storeName}`);
        } catch (error) {
            console.error(`[PrysmDatabase] Error deleting ${key} from ${storeName}:`, error);
            clientLogger.logError('database_delete_error', {
                storeName,
                key,
                error: error.message,
                stack: error.stack
            });
            throw new Error(`Storage operation failed: ${error.message}`);
        }
    }

    /**
     * Clear all data from a "store"
     * @param {string} storeName - Name of the "store" to clear
     * @returns {Promise<void>}
     */
    async clear(storeName) {
        try {
            const prefix = `${DB_NAME}_${storeName}_`;
            const keysToRemove = [];

            // Find all keys that match the store prefix
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(prefix)) {
                    keysToRemove.push(key);
                }
            }

            // Remove all matching keys
            keysToRemove.forEach(key => localStorage.removeItem(key));
            console.log(`[PrysmDatabase] Cleared ${keysToRemove.length} items from ${storeName}`);
        } catch (error) {
            console.error(`[PrysmDatabase] Error clearing ${storeName}:`, error);
            clientLogger.logError('database_clear_error', {
                storeName,
                error: error.message,
                stack: error.stack
            });
            throw new Error(`Storage operation failed: ${error.message}`);
        }
    }

    /**
     * Get all data from a "store"
     * @param {string} storeName - Name of the "store"
     * @returns {Promise<any[]>} All data from the store
     */
    async getAll(storeName) {
        try {
            const prefix = `${DB_NAME}_${storeName}_`;
            const results = [];

            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(prefix)) {
                    try {
                        const data = localStorage.getItem(key);
                        if (data) {
                            results.push(JSON.parse(data));
                        }
                    } catch (parseError) {
                        console.warn(`[PrysmDatabase] Error parsing data for key ${key}:`, parseError);
                    }
                }
            }

            console.log(`[PrysmDatabase] Retrieved ${results.length} items from ${storeName}`);
            return results;
        } catch (error) {
            console.error(`[PrysmDatabase] Error getting all data from ${storeName}:`, error);
            clientLogger.logError('database_getall_error', {
                storeName,
                error: error.message,
                stack: error.stack
            });
            throw new Error(`Storage operation failed: ${error.message}`);
        }
    }

    /**
     * Check if a key exists in a "store"
     * @param {string} storeName - Name of the "store"
     * @param {string} key - Key to check
     * @returns {Promise<boolean>} True if key exists
     */
    async has(storeName, key) {
        try {
            const localStorageKey = `${DB_NAME}_${storeName}_${key}`;
            const exists = localStorage.getItem(localStorageKey) !== null;
            console.log(`[PrysmDatabase] Key ${key} exists in ${storeName}: ${exists}`);
            return exists;
        } catch (error) {
            console.error(`[PrysmDatabase] Error checking if ${key} exists in ${storeName}:`, error);
            clientLogger.logError('database_has_error', {
                storeName,
                key,
                error: error.message,
                stack: error.stack
            });
            throw new Error(`Storage operation failed: ${error.message}`);
        }
    }

    /**
     * Get all keys from a "store"
     * @param {string} storeName - Name of the "store"
     * @returns {Promise<any[]>} All keys from the store
     */
    async getAllKeys(storeName) {
        try {
            const prefix = `${DB_NAME}_${storeName}_`;
            const keys = [];

            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(prefix)) {
                    // Remove the prefix to get the original key
                    const originalKey = key.substring(prefix.length);
                    keys.push(originalKey);
                }
            }

            console.log(`[PrysmDatabase] Retrieved ${keys.length} keys from ${storeName}`);
            return keys;
        } catch (error) {
            console.error(`[PrysmDatabase] Error getting all keys from ${storeName}:`, error);
            clientLogger.logError('database_getallkeys_error', {
                storeName,
                error: error.message,
                stack: error.stack
            });
            throw new Error(`Storage operation failed: ${error.message}`);
        }
    }

    /**
     * Get storage statistics
     * @returns {Promise<Object>} Storage statistics
     */
    async getStats() {
        try {
            const stats = {
                name: DB_NAME,
                version: DB_VERSION,
                stores: STORE_NAMES,
                storeCount: STORE_NAMES.length,
                totalKeys: localStorage.length,
                type: 'localStorage'
            };

            // Get item counts for each store
            for (const storeName of STORE_NAMES) {
                try {
                    const keys = await this.getAllKeys(storeName);
                    stats[`${storeName}_count`] = keys.length;
                } catch (error) {
                    stats[`${storeName}_count`] = 'error';
                }
            }

            console.log('[PrysmDatabase] Storage statistics:', stats);
            return stats;
        } catch (error) {
            console.error('[PrysmDatabase] Failed to get storage statistics:', error);
            clientLogger.logError('database_stats_error', {
                error: error.message,
                stack: error.stack
            });
            throw error;
        }
    }

    /**
     * Clear all data from all "stores"
     * @returns {Promise<void>}
     */
    async clearAll() {
        try {
            console.log('[PrysmDatabase] Clearing all localStorage data...');

            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(`${DB_NAME}_`)) {
                    keysToRemove.push(key);
                }
            }

            keysToRemove.forEach(key => localStorage.removeItem(key));
            console.log(`[PrysmDatabase] Cleared ${keysToRemove.length} items from localStorage`);
        } catch (error) {
            console.error('[PrysmDatabase] Error clearing all data:', error);
            clientLogger.logError('database_clearall_error', {
                error: error.message,
                stack: error.stack
            });
            throw new Error(`Storage operation failed: ${error.message}`);
        }
    }

    /**
     * Delete the entire storage (equivalent to deleting database)
     * @returns {Promise<void>}
     */
    async deleteDatabase() {
        try {
            console.log('[PrysmDatabase] Deleting all localStorage data...');
            await this.clearAll();

            // Clear specific additional keys
            const additionalKeys = ['registration_data', 'auth_token', 'visitorUUID'];
            additionalKeys.forEach(key => {
                localStorage.removeItem(key);
                console.log(`[PrysmDatabase] Removed additional key: ${key}`);
            });

            console.log('[PrysmDatabase] All localStorage data deleted successfully');
        } catch (error) {
            console.error('[PrysmDatabase] Error deleting localStorage data:', error);
            clientLogger.logError('database_delete_error', {
                error: error.message,
                stack: error.stack
            });
            throw new Error(`Storage operation failed: ${error.message}`);
        }
    }

    /**
     * Close storage (no-op for localStorage)
     * @returns {Promise<void>}
     */
    async close() {
        console.log('[PrysmDatabase] localStorage does not need to be closed');
    }
}

// Export singleton instance
export const prysmDB = new PrysmDatabase();

// Export class for testing purposes
export { PrysmDatabase };

// Export constants for external use
export { DB_NAME, DB_VERSION, STORE_NAMES };